<template>
  <div class="route-detail-wrapper" :style="`height: ${isFull ? 'calc(100% - 0px)' : '640px'}`">
    <!-- ===== 上部：左右两栏 ===== -->
    <div class="route-detail-body">
      <!-- 左侧：地图区域 -->
      <div class="map-panel">
        <div
          class="map-container"
          ref="mapContainer"
          @mousedown="handleMouseDown"
          @mousemove="handleMouseMove"
          @mouseup="handleMouseUp"
          @mouseleave="handleMouseLeave"
          @wheel.prevent="handleWheel"
        >
          <svg
            width="100%"
            height="100%"
            :viewBox="`0 0 ${mapNaturalWidth} ${mapNaturalHeight}`"
            preserveAspectRatio="xMidYMid meet"
            class="map-svg"
            style="display:block;"
          >
            <g :transform="`translate(${panX}, ${panY}) scale(${zoom})`">
              <!-- 地图底图 -->
              <image
                v-if="mapConfig.imageUrl"
                :href="mapConfig.imageUrl"
                x="0" y="0"
                :width="mapNaturalWidth"
                :height="mapNaturalHeight"
                preserveAspectRatio="xMidYMid meet"
                draggable="false"
                style="pointer-events: none;"
              />
              <rect v-else width="100%" height="100%" fill="#f5f7fa" />

              <!-- 路径连接线：两层叠影 -->
              <polyline
                v-if="displayWaypoints.length > 1"
                :points="displayWaypoints.map(p => p.px + ',' + p.py).join(' ')"
                fill="none"
                stroke="#409eff"
                stroke-width="4"
                stroke-opacity="0.15"
                stroke-linejoin="round"
                stroke-linecap="round"
                style="pointer-events: none;"
              />
              <polyline
                v-if="displayWaypoints.length > 1"
                :points="displayWaypoints.map(p => p.px + ',' + p.py).join(' ')"
                fill="none"
                stroke="#409eff"
                stroke-width="2"
                stroke-dasharray="6,5"
                stroke-linejoin="round"
                stroke-linecap="round"
                style="pointer-events: none;"
              />

              <!-- 航点组 -->
              <g v-for="(point, index) in displayWaypoints" :key="'wp-' + index" style="pointer-events: none;">
                <circle
                  :cx="point.px"
                  :cy="point.py"
                  r="8"
                  fill="#ffffff"
                  :stroke="index === 0 ? '#67c23a' : index === displayWaypoints.length - 1 ? '#f56c6c' : '#409eff'"
                  stroke-width="2"
                />
                <circle
                  :cx="point.px"
                  :cy="point.py"
                  r="3.5"
                  :fill="index === 0 ? '#67c23a' : index === displayWaypoints.length - 1 ? '#f56c6c' : '#409eff'"
                />

                <g :transform="`translate(${point.px + 12}, ${point.py - 12})`">
                  <rect
                    :x="0"
                    :y="-9"
                    :width="numberWidth(index) + 8"
                    :height="18"
                    rx="9"
                    ry="9"
                    :fill="index === 0 ? '#67c23a' : index === displayWaypoints.length - 1 ? '#f56c6c' : '#409eff'"
                  />
                  <text
                    :x="(numberWidth(index) + 8) / 2"
                    y="4"
                    font-size="10"
                    fill="#ffffff"
                    font-weight="600"
                    text-anchor="middle"
                  >
                    {{ index + 1 }}
                  </text>
                </g>
              </g>
            </g>
          </svg>

          <div class="map-zoom-info">
            <i class="el-icon-zoom-in"></i>
            {{ (zoom * 100).toFixed(0) }}%
          </div>
        </div>
      </div>

      <!-- 右侧：详情面板 -->
      <div class="detail-panel" :style="`width: ${isFull ? '600px' : '550px'}`">
        <div class="detail-scroll">
          <div class="info-section">
            <div class="section-title">
              <i class="el-icon-document"></i>
              {{ $t('smartRouting.mapCenter.pathPlanning.routeManagement.basicInfo') }}
            </div>

            <el-descriptions :column="1" border size="small">
              <el-descriptions-item :label="$t('smartRouting.mapCenter.pathPlanning.routeManagement.routeName')">
                {{ detailData.name || '-' }}
              </el-descriptions-item>
              <el-descriptions-item :label="$t('smartRouting.mapCenter.pathPlanning.routeCreate.associateMap')">
                {{ detailData.mapName || '-' }}
              </el-descriptions-item>
              <el-descriptions-item :label="$t('smartRouting.voiceLibrary.title')">
                {{ detailData.voiceName || detailData.voiceNam || '-' }}
              </el-descriptions-item>
              <el-descriptions-item :label="$t('smartRouting.mapCenter.pathPlanning.routeCreate.routeDesc')">
                {{ detailData.desc || '-' }}
              </el-descriptions-item>
            </el-descriptions>
          </div>

          <div class="waypoints-section">
            <div class="section-title">
              <i class="el-icon-location"></i>
              {{ $t('smartRouting.mapCenter.pathPlanning.routeCreate.waypointList') }}
            </div>

            <el-table
              :data="displayWaypoints"
              size="small"
              class="waypoints-table"
              :max-height="isFull ? 440 : 310"
              stripe
            >
              <el-table-column prop="name" :label="$t('smartRouting.mapCenter.pathPlanning.routeCreate.waypointName')" width="90">
                <template #default="{ row, $index }">
                  <span class="waypoint-index">#{{ $index + 1 }}</span>
                  <span class="waypoint-name">{{ row.name || '-' }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="px" label="x" width="60">
                <template #default="{ row }">{{ row.px ?? '-' }}</template>
              </el-table-column>
              <el-table-column prop="py" label="y" width="60">
                <template #default="{ row }">{{ row.py ?? '-' }}</template>
              </el-table-column>
              <el-table-column prop="worldX" :label="$t('smartRouting.mapCenter.pathPlanning.routeCreate.worldCoordX')" width="70">
                <template #default="{ row }">{{ fmtWorld(row.worldX) }}</template>
              </el-table-column>
              <el-table-column prop="worldY" :label="$t('smartRouting.mapCenter.pathPlanning.routeCreate.worldCoordY')" width="70">
                <template #default="{ row }">{{ fmtWorld(row.worldY) }}</template>
              </el-table-column>
              <el-table-column prop="yaw" label="yaw" width="60">
                <template #default="{ row }">{{ row.yaw ?? 0 }}°</template>
              </el-table-column>
              <el-table-column prop="speechText" :label="$t('smartRouting.mapCenter.pathPlanning.routeCreate.waypointResource')" min-width="80">
                <template #default="{ row }">
                  <el-popover
                    placement="top-start"
                    :title="$t('smartRouting.mapCenter.pathPlanning.routeCreate.speechText')"
                    :width="200"
                    trigger="hover"
                  >
                    <template #default>
                      <div style="max-height: 300px;overflow: auto;">{{row.speechText}}</div>
                    </template>
                    <template #reference>
                      <span class="ellipsis-text">{{ row.speechText }}</span>
                    </template>
                  </el-popover>
                   <el-tag v-if="row.actionName" size="small">{{ row.actionName }}</el-tag>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== 底部操作栏 ===== -->
    <div class="route-detail-footer">
      <div class="footer-info">
        {{ $t('smartRouting.mapCenter.pathPlanning.routeCreate.coordSystemInfo', { res: mapConfig.res, ox: mapConfig.ox, oy: mapConfig.oy, yaw: (mapConfig.yaw0 * 180 / Math.PI).toFixed(1) }) }}
        <br>
        <!-- 比例尺 1m ≈ {{ metersToPixels(1) }} 像素 （1像素 = {{ mapConfig.res }}m）
        <br> -->
        <!-- <span style="color:#c0c4cc">
          ※ 严格按 Nav2 公式：u_grid=(u+0.5)·res，v_grid=(H-1-v)·res
        </span> -->
      </div>
      <div class="footer-actions">
        <el-button type="primary" icon="el-icon-position" @click="handleDeliver">
          {{ $t('smartRouting.mapCenter.pathPlanning.routeManagement.deliver') }}
        </el-button>
        <el-button type="primary" icon="el-icon-edit" @click="handleEdit">
          {{ $t('smartRouting.mapCenter.edit') }}
        </el-button>
        <el-button @click="handleClose">
          {{ $t('smartRouting.mapCenter.mapManagement.mapDetail.close') }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { reqPathDetail } from '@/api/map'

export default {
  name: 'RouteDetail',
  props: {
    routeId: {
      type: [String, Number],
      required: true
    },
    isFullscreen: { type: Boolean, default: false }
  },
  emits: ['close', 'edit', 'deliver', 'export'],
  setup(props, { emit }) {
    const { t } = useI18n()
    const loading = ref(false)
    const isFull = ref(false)

    const detailData = ref({
      code: '',
      name: '',
      status: '',
      pathType: '',
      mapName: '',
      voiceName: '',
      desc: '',
      version: '',
      createBy: '',
      createTime: '',
      updateBy: '',
      updateTime: '',
      simulateStatus: 'risk',
      simulateMessage: '',
    })

    // ===== 地图配置：与 Nav2 PGM+YAML 对应 =====
    const mapConfig = reactive({
      res: 0.05,
      ox: -14.3,
      oy: -5.68,
      yaw0: 0,
      imageUrl: ''
    })

    const mapNaturalWidth = ref(0)
    const mapNaturalHeight = ref(0)

    const mapReady = computed(() =>
      mapNaturalWidth.value > 0 && mapNaturalHeight.value > 0
    )

    // 后端原始航点（只含 worldX/worldY，不依赖 x/y）
    const rawWaypoints = ref([])

    const zoom = ref(1)
    const panX = ref(0)
    const panY = ref(0)

    const isDragging = ref(false)
    const hasMoved = ref(false)
    const dragStartX = ref(0)
    const dragStartY = ref(0)
    const panStartX = ref(0)
    const panStartY = ref(0)

    // ===== 比例尺 =====
    const metersToPixels = (meters) => {
      if (!mapConfig.res) return '—'
      const px = meters / mapConfig.res
      return px >= 100 ? px.toFixed(0) : px.toFixed(1)
    }

    // ===== 世界坐标显示：3 位小数 =====
    const fmtWorld = (v) => {
      if (v == null || !isFinite(v)) return '0.000'
      return Number(v).toFixed(3)
    }

    // ===== 编号胶囊宽度估算 =====
    const numberWidth = (index) => {
      const numStr = String(index + 1)
      return numStr.length * 7
    }

    // ===== 坐标转换：严格按 Nav2 官方公式 =====
    //
    //  u_grid = (u + 0.5) * res0
    //  v_grid = (H - 1 - v) * res0
    //  x_map  = x0 + cos(yaw0)*u_grid - sin(yaw0)*v_grid
    //  y_map  = y0 + sin(yaw0)*u_grid + cos(yaw0)*v_grid
    //
    //  反解：
    //  dx = x_map - x0
    //  dy = y_map - y0
    //  u_grid =  cos(yaw0)*dx + sin(yaw0)*dy
    //  v_grid = -sin(yaw0)*dx + cos(yaw0)*dy
    //  u = u_grid / res0 - 0.5
    //  v = H - 1 - v_grid / res0
    //

    // 世界坐标 -> 图像像素
    const worldToPixel = (wx, wy) => {
      if (!mapReady.value) return { pixelX: 0, pixelY: 0 }
      const H = mapNaturalHeight.value
      const res0 = mapConfig.res
      const x0 = mapConfig.ox
      const y0 = mapConfig.oy
      const yaw0 = mapConfig.yaw0 || 0

      const dx = wx - x0
      const dy = wy - y0

      const cosYaw = Math.cos(yaw0)
      const sinYaw = Math.sin(yaw0)

      const uGrid =  cosYaw * dx + sinYaw * dy
      const vGrid = -sinYaw * dx + cosYaw * dy

      const u = uGrid / res0 - 0.5
      const v = H - 1 - vGrid / res0

      const W = mapNaturalWidth.value
      return {
        pixelX: Math.min(W - 1, Math.max(0, Math.round(u))),
        pixelY: Math.min(H - 1, Math.max(0, Math.round(v)))
      }
    }

    // ✅ 显示用航点：由 worldX/worldY 反算像素，不依赖后端 x/y
    const displayWaypoints = computed(() => {
      if (!mapReady.value) {
        return rawWaypoints.value.map(w => ({
          ...w,
          px: w.x ?? 0,
          py: w.y ?? 0
        }))
      }
      return rawWaypoints.value.map(w => {
        const wx = Number(w.worldX)
        const wy = Number(w.worldY)
        if (!isFinite(wx) || !isFinite(wy)) {
          return { ...w, px: w.x ?? 0, py: w.y ?? 0 }
        }
        const { pixelX, pixelY } = worldToPixel(wx, wy)
        return { ...w, px: pixelX, py: pixelY }
      })
    })

    // ===== 加载图片尺寸 =====
    const loadMapSize = (url) => {
      if (!url) {
        mapNaturalWidth.value = 0
        mapNaturalHeight.value = 0
        return
      }
      const img = new Image()
      img.onload = () => {
        mapNaturalWidth.value = img.naturalWidth || 0
        mapNaturalHeight.value = img.naturalHeight || 0
      }
      img.onerror = () => {
        mapNaturalWidth.value = 0
        mapNaturalHeight.value = 0
      }
      img.src = url
    }

    const applyMapInfo = (mapInfo) => {
      if (!mapInfo) return
      mapConfig.imageUrl = mapInfo.mapImageUrl || ''
      if (mapInfo.resolution != null) mapConfig.res = parseFloat(mapInfo.resolution)
      if (mapInfo.originX != null) mapConfig.ox = parseFloat(mapInfo.originX)
      if (mapInfo.originY != null) mapConfig.oy = parseFloat(mapInfo.originY)
      if (mapInfo.originYaw != null) {
        mapConfig.yaw0 = parseFloat(mapInfo.originYaw)
      } else if (mapInfo.yaw0 != null) {
        mapConfig.yaw0 = parseFloat(mapInfo.yaw0)
      }
      loadMapSize(mapConfig.imageUrl)
    }

    const fetchDetail = async () => {
      if (!props.routeId) return
      loading.value = true
      try {
        const res = await reqPathDetail(props.routeId)
        const data = res.data || res

        detailData.value = {
          ...data,
          simulateStatus: data.simulateStatus || 'risk',
          simulateMessage: data.simulateMessage,
          voiceName: data.voiceName || data.voiceNam || ''
        }

        applyMapInfo(data.mapInfo)
        rawWaypoints.value = data.waypoints || []
      } catch (error) {
        console.error('获取详情失败:', error)
        ElMessage.error(error)
      } finally {
        loading.value = false
      }
    }

    // ===== 鼠标事件 =====
    const handleMouseDown = (e) => {
      if (e.target.closest && e.target.closest('.el-button')) return
      e.preventDefault()
      isDragging.value = true
      hasMoved.value = false
      dragStartX.value = e.clientX
      dragStartY.value = e.clientY
      panStartX.value = panX.value
      panStartY.value = panY.value
    }

    const handleMouseMove = (e) => {
      if (!isDragging.value) return

      const dx = e.clientX - dragStartX.value
      const dy = e.clientY - dragStartY.value

      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) hasMoved.value = true

      const svg = document.querySelector('.map-svg')
      if (!svg || !mapReady.value) return
      const rect = svg.getBoundingClientRect()
      const scaleX = mapNaturalWidth.value / rect.width
      const scaleY = mapNaturalHeight.value / rect.height

      panX.value = panStartX.value + dx * scaleX * zoom.value
      panY.value = panStartY.value + dy * scaleY * zoom.value
      clampPan()
    }

    const handleMouseUp = () => {
      isDragging.value = false
      setTimeout(() => { hasMoved.value = false }, 50)
    }

    const handleMouseLeave = () => {
      isDragging.value = false
    }

    // ===== 滚轮缩放（以鼠标位置为中心） =====
    const handleWheel = (event) => {
      if (!mapReady.value) return
      const delta = event.deltaY > 0 ? 0.9 : 1.1
      const newZoom = Math.min(5, Math.max(0.2, zoom.value * delta))
      const vbWidth = mapNaturalWidth.value
      const vbHeight = mapNaturalHeight.value

      const svg = document.querySelector('.map-svg')
      if (!svg) return
      const rect = svg.getBoundingClientRect()
      const percentX = (event.clientX - rect.left) / rect.width
      const percentY = (event.clientY - rect.top) / rect.height

      const beforeX = (percentX * vbWidth - panX.value) / zoom.value
      const beforeY = (percentY * vbHeight - panY.value) / zoom.value

      zoom.value = newZoom
      panX.value = percentX * vbWidth - beforeX * zoom.value
      panY.value = percentY * vbHeight - beforeY * zoom.value

      clampPan()
    }

    const clampPan = () => {
      if (!mapReady.value) return
      const vbWidth = mapNaturalWidth.value
      const vbHeight = mapNaturalHeight.value
      const maxPanX = vbWidth * (zoom.value - 1) / 2
      const maxPanY = vbHeight * (zoom.value - 1) / 2
      panX.value = Math.max(-maxPanX, Math.min(maxPanX, panX.value))
      panY.value = Math.max(-maxPanY, Math.min(maxPanY, panY.value))
    }

    const handleClose = () => {
      emit('close')
    }

    const handleEdit = () => {
      emit('edit', detailData.value)
    }

    const handleDeliver = () => {
      emit('deliver', detailData.value)
    }

    watch(() => props.routeId, () => {
      if (props.routeId) {
        fetchDetail()
      }
    }, { immediate: true })

    watch(() => props.isFullscreen, () => {
      isFull.value = props.isFullscreen
    }, { immediate: true })

    onMounted(() => {
      if (props.routeId) {
        fetchDetail()
      }
    })

    return {
      loading,
      detailData,
      mapConfig,
      mapNaturalWidth,
      mapNaturalHeight,
      mapReady,
      rawWaypoints,
      displayWaypoints,
      zoom,
      panX,
      panY,
      isFull,
      handleMouseDown,
      handleMouseMove,
      handleMouseUp,
      handleMouseLeave,
      handleWheel,
      metersToPixels,
      fmtWorld,
      numberWidth,
      handleClose,
      handleEdit,
      handleDeliver,
    }
  }
}
</script>

<style scoped>
/* ===== 整体 ===== */
.route-detail-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f5f7fa;
}

.route-detail-body {
  flex: 1;
  display: flex;
  min-height: 0;
  overflow: hidden;
}

/* ===== 左侧：地图区域 ===== */
.map-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 10px;
  margin: 12px 6px 0 12px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  min-width: 400px;
}
.map-container {
  flex: 1;
  position: relative;
  min-height: 400px;
  background: #fafbfc;
  overflow: hidden;
  cursor: grab;
  user-select: none;
}
.map-container:active {
  cursor: grabbing;
}
.map-svg {
  display: block;
  width: 100%;
  height: 100%;
}

.map-zoom-info {
  position: absolute;
  bottom: 14px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.9);
  color: #409eff;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 12px;
  font-family: 'Courier New', monospace;
  box-shadow: 0 2px 6px rgba(64, 158, 255, 0.15);
  border: 1px solid rgba(64, 158, 255, 0.2);
  pointer-events: none;
  backdrop-filter: blur(4px);
}
.map-zoom-info .el-icon-zoom-in {
  font-size: 13px;
}

/* ===== 右侧：详情面板 ===== */
.detail-panel {
  width: 520px;
  background: #ffffff;
  border-radius: 10px;
  margin: 12px 12px 0 6px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  overflow: hidden;
}
.detail-scroll {
  flex: 1;
  padding: 16px 20px 16px;
}
.detail-scroll :deep(.el-scrollbar__wrap) {
  overflow-x: hidden;
}

/* ===== 信息区块 ===== */
.info-section {
  margin-bottom: 16px;
}
.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #1d2129;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.section-title .el-icon-document,
.section-title .el-icon-location {
  color: #409eff;
}

/* ===== 航点表格 ===== */
.waypoints-section {
  margin-bottom: 16px;
}
.waypoints-table {
  width: 100%;
}
.waypoints-table :deep(.el-table__header th) {
  background: #f7f9fb;
  font-size: 12px;
  color: #606266;
  font-weight: 500;
}
.waypoints-table :deep(.el-table__body td) {
  font-size: 12px;
  padding: 4px 0;
}
.waypoint-index {
  color: #409eff;
  font-weight: 500;
  margin-right: 4px;
}
.waypoint-name {
  color: #1d2129;
}
.text-muted {
  color: #c0c4cc;
}

/* ===== 底部操作栏 ===== */
.route-detail-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  background: #ffffff;
  border-top: 1px solid #ebeef5;
  box-shadow: 0 -1px 4px rgba(0, 0, 0, 0.04);
  flex-shrink: 0;
  margin-top: 12px;
}
.footer-info {
  font-size: 12px;
  color: #86909c;
  font-family: 'Courier New', monospace;
  line-height: 1.6;
}
.footer-actions {
  display: flex;
  gap: 10px;
}

.ellipsis-text {
  display: inline-block;
  max-width: 150px;       /* 根据实际情况调整 */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

/* ===== 响应式 ===== */
@media (max-width: 1024px) {
  .detail-panel { width: 400px; }
}
@media (max-width: 768px) {
  .route-detail-body { flex-direction: column; }
  .map-panel { margin: 8px; min-height: 300px; }
  .detail-panel { width: auto; margin: 0 8px; }
  .route-detail-footer { flex-direction: column; gap: 8px; align-items: flex-start; }
}
</style>